import { useQuery, QueryResult } from '@apollo/client';
import { useObserver } from 'mobx-react';

import useCart from 'hooks/use-cart';
import { useCatalogRequestContext } from 'src/contexts/catalog-request-context';
import useUser from 'src/hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { GetSpecialMenuCardsDocument, GetSpecialMenuCardsQuery, SpecialMenuCard } from 'src/gql/graphql';
import { toUppercaseMenuType } from 'shared/constants';
import { useStores } from 'contexts/stores';
import { determinePlatformType } from 'shared/helpers/enum-mappings';
import { useGetDegradedPollInterval } from 'shared/hooks/use-get-degraded-poll-interval';
import { SPECIALS_POLL_INTERVAL_MS, SPECIALS_POLL_DEGRADATION_MULTIPLIER } from './specials-polling.constants';

type UseSpecialMenuCardsProps = {
  specialId?: string;
  skip?: boolean;
};

type UseSpecialMenuCardsResult = Omit<QueryResult<GetSpecialMenuCardsQuery>, 'data' | 'error' | 'loading'> & {
  specials: SpecialMenuCard[];
  hasSpecials: boolean;
  loading: boolean;
  error: unknown;
};

/**
 * Hook to fetch simplified special menu card data
 * This is a lighter alternative to useDispensarySpecials when only menu display data is needed
 *
 * @param {Object} [props] - Optional configuration object
 * @param {string} [props.specialId] - Specific special ID to fetch
 * @param {boolean} [props.skip] - Skip the query
 * @returns {Object} { specials, hasSpecials, loading, error }
 */
export default function useSpecialMenuCards(props: UseSpecialMenuCardsProps = {}): UseSpecialMenuCardsResult {
  const dispensaryHookData = useDispensary();
  // These are used to account for the various places where the hook could be called from
  const { UI } = useStores();
  const uiDispensary = useObserver(() => UI?.dispensary);
  const isKiosk = useObserver(() => UI?.isKiosk ?? false);

  const cart = useCart();
  const cartDispensary = useObserver(() => cart.dispensary);
  const platformType = determinePlatformType(UI);

  const dispensary = dispensaryHookData.dispensary ?? uiDispensary ?? cartDispensary;
  const { menuType } = useCatalogRequestContext();

  // Get user ID for customer segment filtering
  const user = useUser();
  const userId = useObserver(() => user?.id);

  const { specialId, skip: skipQuery = false } = props;

  const basePollInterval = useGetDegradedPollInterval({
    milliseconds: SPECIALS_POLL_INTERVAL_MS,
    degradationMultiplier: SPECIALS_POLL_DEGRADATION_MULTIPLIER,
  });
  const pollInterval = isKiosk ? basePollInterval : 0;

  const variables = {
    dispensaryId: dispensary?.id ?? '',
    menuType: toUppercaseMenuType(menuType),
    platformType,
    ...(specialId && { specialId }),
    ...(userId && { ecomUserId: userId }),
  };

  const { data, loading, error, ...apolloValues } = useQuery(GetSpecialMenuCardsDocument, {
    skip: !dispensary || skipQuery,
    variables,
    fetchPolicy: pollInterval > 0 ? 'cache-and-network' : undefined,
    pollInterval,
  });

  // Response is already sorted by displayRank from backend
  const cards = (data?.getSpecialMenuCards?.menuCards ?? []).filter((card): card is SpecialMenuCard => card !== null);
  const hasSpecials = cards.length > 0;

  return {
    specials: cards,
    hasSpecials,
    loading,
    error,
    ...apolloValues,
  };
}
